/**
 * QMP-FE-8d — STEPS 2–4 AS THE v83 LISTS (docs/design/mockups/
 * mockup-qmp-v83-official.html: `inspectionPage` / `controlsPage` /
 * `handoverPage` l. 1826–1859, `row` l. 1795–1802; dark values of the
 * `#cqa-qmp-flow` tokens). Each rule is a row: its TITLE and MEANING as the
 * server serves them (`standard_steps[].title / meaning`), the sentence of the
 * common rule, where it differs by package, "Needs review in …", and the
 * button that opens TODAY's editor of that subarea (`onEdit(section)`).
 *
 * READ ONLY: every word comes from `lib/qmpRuleRows` over the editor's state
 * and the served choices; nothing here saves. "Handover lots" reads
 * `asset_group_count` from `GET /projects/{p}/qwps`. The handover unit is
 * never shown (owner, 2026-11-04: the v6 lots are not approved yet), so v83's
 * "Where are handover lots set? →" note (Together / One by one) is left out.
 */
import React from 'react';
import { listQwps } from '../../lib/api';
import {
  ruleRow, countLine, flaggedRows, needsReviewText, handoverLine, followLine,
  ruleSummary, punchShort, lotsText,
} from '../../lib/qmpRuleRows';
import { companyNames } from './PublishStep';

// v83 tokens, dark values.
const H2 = 'text-[20px] leading-[1.3] mb-[7px] font-bold';
const H3 = 'text-[15px] mb-[5px] font-bold';
const INTRO = 'text-[#afbdd0] max-w-[72ch]';
const PANEL = 'bg-[#101a2b] border border-[#2b3c56] rounded-[10px] p-[18px] my-[16px] max-[580px]:p-[14px]';
const PANEL_HEAD = 'flex items-start justify-between gap-[12px] flex-wrap mb-[12px]';
const SMALL = 'text-[12px] text-[#afbdd0]';
const RULE_ROW = 'flex items-center justify-between gap-[12px] py-[15px] border-t border-[#2b3c56] '
  + 'first:border-t-0 first:pt-0 last:pb-0 max-[580px]:items-start max-[580px]:flex-wrap';
const VALUE = 'my-[5px] text-[#f1f5fb]';
const FLAG = 'text-[12px] text-[#ffa9b2] mt-[3px]';
const BUTTON = 'min-h-[44px] border border-[#2b3c56] rounded-[7px] px-[13px] py-[9px] text-[#f1f5fb] bg-[#0b1423] '
  + 'whitespace-normal shrink-0';
const TABLE_BUTTON = 'min-h-[40px] border border-[#2b3c56] rounded-[7px] p-[8px] text-[12px] text-[#f1f5fb] bg-[#0b1423] '
  + 'max-[580px]:min-h-[44px]';
const INFO = 'border-l-[3px] border-[#ff8b32] px-[13px] py-[10px] bg-[#32251e] my-[14px] text-[#f1f5fb]';
const BADGE = 'inline-block px-[9px] py-[4px] rounded-[5px] text-[12px] font-bold';
const LABEL = 'block text-[#afbdd0] text-[12px] mb-[3px]';
const TH = 'text-left text-[#afbdd0] text-[11px] font-bold px-[8px] py-[10px] border-b border-[#2b3c56] '
  + 'first:pl-0 last:pr-0';
const TD = 'px-[8px] py-[11px] align-middle border-b border-[#2b3c56] first:pl-0 last:pr-0 '
  + 'max-[580px]:block max-[580px]:w-full max-[580px]:px-0 max-[580px]:py-[4px] max-[580px]:border-0';
const SOURCE = 'block text-[11px] mt-[3px]';

const titleOf = (served, section) => ((served || []).find((s) => s && s.section === section) || {});

function RuleRow({
  section, editor, served, readOnly, onEdit,
}) {
  const names = companyNames(editor);
  const st = titleOf(served, section);
  const r = ruleRow(section, editor.states[section], editor.ctx.structure,
    (editor.status[section] || {}).review, { options: editor.options, names, readOnly });
  return (
    <div className={RULE_ROW} data-testid={`ql-row-${section}`}>
      <div className="max-[580px]:w-full">
        <div className="font-bold" data-testid={`ql-title-${section}`}>{st.title}</div>
        <div className={SMALL} data-testid={`ql-meaning-${section}`}>{st.meaning}</div>
        <div className={VALUE} data-testid={`ql-value-${section}`}>{r.value}</div>
        <div className={SMALL} data-testid={`ql-count-${section}`}>{r.count}</div>
        {r.flag && <div className={FLAG} data-testid={`ql-flag-${section}`}>{r.flag}</div>}
      </div>
      <button type="button" className={BUTTON} onClick={() => onEdit(section)} data-testid={`ql-edit-${section}`}>
        {r.action}
      </button>
    </div>
  );
}

function InspectionPage(props) {
  return (
    <>
      <h2 className={H2}>2. Inspections &amp; sign-offs</h2>
      <p className={INTRO}>
        Who prepares the Inspection and Test Plan (ITP), how much notice to give, and when work can continue.
      </p>
      <section className={PANEL} data-testid="ql-panel-inspections">
        <RuleRow section="itp_rules" {...props} />
        <RuleRow section="hold_points" {...props} />
        <RuleRow section="witness_points" {...props} />
      </section>
      <details className="border-t border-[#2b3c56] mt-[15px] pt-[12px]">
        <summary className="min-h-[36px] text-[#ff8b32] text-[13px] cursor-pointer">
          What is the difference between an ITP and an ITC?
        </summary>
        <p className="mt-[10px] text-[13px] text-[#afbdd0]">
          The ITP is the plan and its control points. The ITC records the work as it is done, with the
          sign-offs it needs. The QMP rules set how those controls run in every package.
        </p>
      </details>
    </>
  );
}

function ControlsPage(props) {
  return (
    <>
      <h2 className={H2}>3. NCRs, RFIs &amp; permissions</h2>
      <p className={INTRO}>
        How deviations and questions are handled, who else can see the records, and how AI may help.
      </p>
      <section className={PANEL} data-testid="ql-panel-ncr-rfi">
        <RuleRow section="ncr" {...props} />
        <RuleRow section="rfi" {...props} />
      </section>
      <section className={PANEL} data-testid="ql-panel-access">
        <h3 className={H3}>Access and assistance</h3>
        <RuleRow section="visibility" {...props} />
        <RuleRow section="ai" {...props} />
      </section>
    </>
  );
}

/** `{qwp_id: asset_group_count}` from the QWP list (read only). */
function useLotCounts(projectId) {
  const [counts, setCounts] = React.useState({});
  React.useEffect(() => {
    let cancelled = false;
    Promise.resolve(listQwps(projectId))
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        const out = {};
        rows.forEach((q) => { if (q && q.id) out[q.id] = q.asset_group_count; });
        if (!cancelled) setCounts(out);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);
  return counts;
}

function HandoverPage({
  editor, served, readOnly, onEdit, projectId, revision,
}) {
  const lots = useLotCounts(projectId);
  const structure = editor.ctx.structure || [];
  const punch = editor.states.punch_list;
  const ps = punch ? punch.general : null;
  const h = (ps && ps.handovers) || [];
  const differs = Boolean(ps && ps.per_handover);
  const punchStatus = editor.status.punch_list || {};
  const punchFlag = needsReviewText(flaggedRows(punchStatus.review, structure));
  const punchTitle = titleOf(served, 'punch_list');
  const handover = editor.states.handover;
  const hReview = (editor.status.handover || {}).review;
  let badge = 'Not saved yet';
  if (readOnly && revision) badge = `Revision ${revision}`;
  else if (punchStatus.dirty) badge = 'Unsaved changes';
  else if (punchStatus.saved) badge = 'Saved in draft';
  let qaNote = 'The standard rule. A package can allow Category B or C items to stay open.';
  if (differs) qaNote = 'With two handovers the rule differs between them. Open items stay open and visible until they are closed.';
  else if (h[0] && h[0].qa_complete_with_open_items) qaNote = 'Open items stay open and visible until they are closed.';
  return (
    <>
      <h2 className={H2}>4. Handover &amp; acceptance</h2>
      <p className={INTRO}>
        Set when an asset is ready to be handed over, who hands it over to whom, and how much notice the
        receiving company gets.
      </p>

      <section className={PANEL} data-testid="ql-panel-punch">
        <div className={PANEL_HEAD}>
          <div>
            <h3 className={H3} data-testid="ql-title-punch_list">{punchTitle.title}</h3>
            <p className={SMALL}>Punch list items are raised at the handover inspection, against the asset.</p>
          </div>
          <span
            className={`${BADGE} ${badge === 'Saved in draft' || badge.startsWith('Revision') ? 'text-[#78d4ae] bg-[#122d28]' : 'bg-[#162238]'}`}
            data-testid="ql-punch-badge"
          >
            {badge}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-[16px] max-[580px]:grid-cols-1 max-[580px]:gap-0">
          <div>
            <span className={LABEL}>Handover</span>
            <strong>Category A blocks it</strong>
            <p className={SMALL} data-testid="ql-meaning-punch_list">{punchTitle.meaning}</p>
          </div>
          <div>
            <span className={LABEL}>QA Complete</span>
            <strong data-testid="ql-value-punch_list">
              {differs ? (
                <>
                  {`Handover 1: ${punchShort(h[0])}`}
                  <br />
                  {`Handover 2: ${punchShort(h[1])}`}
                </>
              ) : punchShort(h[0])}
            </strong>
            <p className={SMALL}>{qaNote}</p>
          </div>
        </div>
        <div className={INFO}>
          <span data-testid="ql-count-punch_list">{countLine('punch_list', punch, structure)}</span>
          {punchFlag && <div className={FLAG} data-testid="ql-flag-punch_list">{punchFlag}</div>}
        </div>
        <button type="button" className={BUTTON} onClick={() => onEdit('punch_list')} data-testid="ql-edit-punch_list">
          {readOnly ? 'View rule' : 'Edit rule'}
        </button>
      </section>

      <section className={PANEL} data-testid="ql-panel-handover">
        <div className={PANEL_HEAD}>
          <div>
            <h3 className={H3}>Handover by Quality Work Package</h3>
            <p className={SMALL} data-testid="ql-meaning-handover">{titleOf(served, 'handover').meaning}</p>
          </div>
        </div>
        <div className={RULE_ROW} data-testid="ql-row-handover">
          <div className="max-[580px]:w-full">
            <strong>Common project rule</strong>
            <div className={VALUE} data-testid="ql-value-handover">
              {ruleSummary('handover', handover ? handover.general : null, { options: editor.options })}
            </div>
            <div className={SMALL} data-testid="ql-count-handover">{followLine(handover, structure)}</div>
          </div>
          <button type="button" className={BUTTON} onClick={() => onEdit('handover')} data-testid="ql-edit-handover">
            {readOnly ? 'View' : 'Edit common rule'}
          </button>
        </div>
        <table className="w-full border-collapse table-fixed text-[13px] max-[580px]:block" data-testid="ql-handover-table">
          <thead className="max-[580px]:hidden">
            <tr>
              <th className={`${TH} w-[22%]`}>Quality Work Package</th>
              <th className={TH}>Who hands over → who receives</th>
              <th className={`${TH} w-[17%]`}>Notice</th>
              <th className={`${TH} w-[14%]`}>Handover lots</th>
              <th className={`${TH} w-[76px]`}>Action</th>
            </tr>
          </thead>
          <tbody className="max-[580px]:block">
            {structure.map((row) => {
              const l = handoverLine(handover, row, hReview, { options: editor.options, readOnly });
              return (
                <tr key={row.qwp_id} className="max-[580px]:block max-[580px]:py-[12px] max-[580px]:border-b max-[580px]:border-[#2b3c56]" data-testid={`ql-handover-${row.qwp_id}`}>
                  <td className={`${TD} font-bold`}>{row.name}</td>
                  <td className={TD} data-label="Hands over → receives">
                    <span data-testid={`ql-route-${row.qwp_id}`}>
                      {l.route.map((p, i) => (
                        <React.Fragment key={`${p.text}-${i}`}>
                          {i > 0 && ' → '}
                          <span className={p.missing ? 'text-[#ffa9b2]' : ''}>{p.text}</span>
                        </React.Fragment>
                      ))}
                    </span>
                    <span
                      className={`${SOURCE} ${l.flagged ? 'text-[#ffa9b2]' : 'text-[#afbdd0]'}`}
                      data-testid={`ql-source-${row.qwp_id}`}
                    >
                      {l.source}
                    </span>
                  </td>
                  <td className={TD} data-label="Notice" data-testid={`ql-notice-${row.qwp_id}`}>
                    {l.notice.map((n, i) => (
                      <React.Fragment key={`${n.n}-${i}`}>
                        {i > 0 && <br />}
                        {n.n != null && <span className={SMALL}>{`${n.n} · `}</span>}
                        {n.text}
                      </React.Fragment>
                    ))}
                  </td>
                  <td className={TD} data-label="Handover lots" data-testid={`ql-lots-${row.qwp_id}`}>
                    {lotsText(lots[row.qwp_id])}
                  </td>
                  <td className={TD}>
                    <button
                      type="button"
                      className={TABLE_BUTTON}
                      onClick={() => onEdit('handover')}
                      data-testid={`ql-edit-handover-${row.qwp_id}`}
                    >
                      {l.action}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}

/** `page` = 2 | 3 | 4. `served` = `GET /qmp` `standard_steps`. */
export default function RuleListPage({
  page, editor, served, readOnly, onEdit, projectId, revision,
}) {
  const props = {
    editor, served, readOnly, onEdit,
  };
  return (
    <div data-testid={`ql-page-${page}`}>
      {page === 2 && <InspectionPage {...props} />}
      {page === 3 && <ControlsPage {...props} />}
      {page === 4 && <HandoverPage {...props} projectId={projectId} revision={revision} />}
    </div>
  );
}
